import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Stack,
  CircularProgress,
  Box
} from '@mui/material';
import { fetchDoctors, resendDiscountOTP } from 'utils/apiHelper';
import { openSnackbar } from 'api/snackbar';

const OTPApprovalModal = ({ 
  open,
  onClose,
  onOTPApproval,
  onSendOTP,
  isSubmitting,
  isOTPSent,
  token,
  messageId,
  showDoctorSelection = true,
  title = "OTP Approval Required",
  description,
  submitButtonText = "Submit OTP & Create Order",
  infoText = "OTP has been sent. Please enter the OTP to proceed with the order.",
  allowResend = true
}) => {
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const loadDoctors = async () => {
      if (showDoctorSelection && open) {
        try {
          setLoading(true);
          const data = await fetchDoctors();
          setDoctors(data);
        } catch (error) {
          openSnackbar({
            open: true,
            message: 'Failed to load doctors list',
            variant: 'alert',
            alert: { color: 'error' }
          });
        } finally {
          setLoading(false);
        }
      }
    };

    loadDoctors();
  }, [showDoctorSelection, open]);

  // Resend timer effect
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Start timer when OTP is sent, reset OTP input when transitioning states
  useEffect(() => {
    if (isOTPSent && messageId) {
      setResendTimer(60); // 1 minute timer
    } else if (!isOTPSent) {
      // Reset OTP input when transitioning back to doctor selection
      setOtpInput('');
      setResendTimer(0);
    }
  }, [isOTPSent, messageId]);

  // Reset states when modal opens or when starting fresh OTP flow
  useEffect(() => {
    if (open) {
      // Reset OTP input and other states when modal opens
      setOtpInput('');
      setResendTimer(0);
      setIsResending(false);
      
      // If not in OTP sent mode, also reset doctor selection
      if (!isOTPSent) {
        setSelectedDoctor('');
      }
    }
  }, [open, isOTPSent]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      setResendTimer(0);
      setIsResending(false);
    };
  }, []);

  const handleSendOTP = async () => {
    if (showDoctorSelection && !selectedDoctor) {
      openSnackbar({
        open: true,
        message: 'Please select a doctor first',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    try {
      await onSendOTP(selectedDoctor);
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to send OTP for approval',
        variant: 'alert',
        alert: { color: 'error' }
      });
      throw error;
    }
  };

  const handleResendOTP = async () => {
    if (!messageId) {
      openSnackbar({
        open: true,
        message: 'No message ID available for resend',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    try {
      setIsResending(true);
      const response = await resendDiscountOTP(messageId);
      
      openSnackbar({
        open: true,
        message: response.message || 'OTP resent successfully',
        variant: 'alert',
        alert: { color: 'success' }
      });
      
      // Reset timer for another minute
      setResendTimer(60);
      
    } catch (error) {
      openSnackbar({
        open: true,
        message: 'Failed to resend OTP',
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleOTPSubmit = () => {
    if (!otpInput.trim()) {
      openSnackbar({
        open: true,
        message: 'Please enter OTP',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    onOTPApproval({
      approver: selectedDoctor,
      message_id: messageId,
      otp_input: otpInput
    });
  };

  const handleClose = () => {
    setSelectedDoctor('');
    setOtpInput('');
    setResendTimer(0);
    setIsResending(false);
    onClose();
  };

  const defaultDescription = showDoctorSelection 
    ? 'Discount requires doctor approval. Please select a doctor and enter OTP.'
    : 'Discount requires owner approval. Please enter the OTP sent to the owner.';
  const displayDescription = description !== undefined ? description : defaultDescription;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {title}
        </Typography>
        {displayDescription && (
          <Typography variant="body2" color="text.secondary">
            {displayDescription}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {showDoctorSelection && !isOTPSent && (
            <FormControl fullWidth required>
              <InputLabel>Select Doctor for Approval</InputLabel>
              <Select
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                disabled={isSubmitting || loading}
              >
                {loading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading doctors...
                  </MenuItem>
                ) : (
                  doctors
                    .sort((a, b) => a.full_name.localeCompare(b.full_name))
                    .map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.full_name} - {doctor?.doctor_speciality || 'General'}
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
          )}

          {!isOTPSent ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendOTP}
              disabled={isSubmitting || (showDoctorSelection && !selectedDoctor)}
              fullWidth
              size="large"
            >
              Send OTP for Approval
            </Button>
          ) : (
            <Box>
              <TextField
                fullWidth
                label="Enter OTP"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value)}
                placeholder="Enter 6-digit OTP"
                disabled={isSubmitting}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                color="success"
                onClick={handleOTPSubmit}
                disabled={!otpInput.trim() || isSubmitting}
                fullWidth
                size="large"
              >
                {isSubmitting ? 'Processing...' : submitButtonText}
              </Button>
            </Box>
          )}

          {isOTPSent && (
            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                {infoText}
              </Alert>
              
              {allowResend && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, mb: 1 }}>
                  {isResending ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                      <CircularProgress size={14} sx={{ mr: 1 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Resending...
                      </Typography>
                    </Box>
                  ) : resendTimer > 0 ? (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem', 
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      Didn't receive OTP? Resend in 
                      <Box 
                        component="span" 
                        sx={{ 
                          mx: 0.5, 
                          fontWeight: 600, 
                          color: 'primary.main',
                          minWidth: '20px',
                          textAlign: 'center'
                        }}
                      >
                        {resendTimer}s
                      </Box>
                    </Typography>
                  ) : (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.secondary'
                      }}
                    >
                      Didn't receive OTP?{' '}
                      <Box
                        component="span"
                        onClick={handleResendOTP}
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontWeight: 500,
                          textDecoration: 'underline',
                          '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'none'
                          }
                        }}
                      >
                        Resend OTP
                      </Box>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OTPApprovalModal;
